import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import * as translator from "../../../../../utils/translator";
import { MyAccountStoreClientConfig } from "../types";
import { MainTracker } from "../../../../../PixelTrackers/MainTracker";

interface Props {
  language: string;
  country: string;
  config: MyAccountStoreClientConfig;
  onRequestOpenMenu?: () => void;
  onRequestCloseMenu?: () => void;
  onActiveChange?: (active: boolean) => void;
}

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const TOOLTIP_WIDTH = 354;
const VIEWPORT_PADDING = 16;

// Frosted-glass blur applied to the page behind the walkthrough while it's active.
const BACKDROP_BLUR = "blur(6px)";

// Figma Elevation/E300 — the tooltip card's drop shadow (node 31796:6720).
const TOOLTIP_SHADOW =
  "0px 0px 1px 0px rgba(5,27,55,0.16), 0px 8px 16px 0px rgba(5,27,55,0.08)";

const isWalkMeSeen = (storageKey: string): boolean => {
  try {
    return window.localStorage.getItem(storageKey) === "1";
  } catch {
    return true; // if localStorage is unavailable, behave as if seen so we never get stuck
  }
};

const markWalkMeSeen = (storageKey: string): void => {
  try {
    window.localStorage.setItem(storageKey, "1");
  } catch {
    /* no-op */
  }
};

const MyAccountStoreWalkMe: React.FC<Props> = ({ language, country, config, onRequestOpenMenu, onRequestCloseMenu, onActiveChange }) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const navigate = useNavigate();

  const measureAnchor = useCallback(
    (retryCount = 0): void => {
      const el = document.getElementById(config.walkMe.targetMenuLinkId);
      if (!el) {
        if (retryCount < 10) {
          setTimeout(() => measureAnchor(retryCount + 1), 250);
        } else {
          setAnchor(null);
        }
        return;
      }
      const rect = el.getBoundingClientRect();
      setAnchor({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    },
    [config.walkMe.targetMenuLinkId],
  );

  useEffect(() => {
    if (!config.allowedCountries.includes(country)) return;
    if (isWalkMeSeen(config.walkMe.seenStorageKey)) return;
    setVisible(true);
  }, [config.allowedCountries, config.walkMe.seenStorageKey, country]);

  useEffect(() => {
    if (!visible) return;
    onActiveChange?.(true);
    onRequestOpenMenu?.();
    measureAnchor();
    const settleTimer = setTimeout(() => measureAnchor(), 350);
    const onReflow = (): void => measureAnchor();
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      onActiveChange?.(false);
      clearTimeout(settleTimer);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [visible, measureAnchor, onRequestOpenMenu, onActiveChange]);

  // Lock page scroll while the walkthrough is active so the highlighted anchor
  // stays put under the spotlight (matches the Sheet modal's scroll-lock).
  useEffect(() => {
    if (!visible) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  if (!visible) return null;

  const dismiss = (): void => {
    markWalkMeSeen(config.walkMe.seenStorageKey);
    setVisible(false);
    onRequestCloseMenu?.();
  };

  const tryStore = (): void => {
    markWalkMeSeen(config.walkMe.seenStorageKey);
    setVisible(false);
    onRequestCloseMenu?.();
    navigate(`/${country}/account/${config.menuLinkId}`);
  };

  const title = translator.translate(language, "my_account", config.walkMe.titleTranslationKey);
  const body = translator.translate(language, "my_account", config.walkMe.bodyTranslationKey);
  const ctaLabel = translator.translate(language, "my_account", config.walkMe.ctaTranslationKey);
  const dismissLabel = translator.translate(language, "my_account", config.walkMe.dismissTranslationKey);

  const handleDismissClick = (): void => {
    MainTracker.track("custom", "Funnel WalkMe Test Lab", { Button_Click: dismissLabel });
    dismiss();
  };

  const handleCtaClick = (): void => {
    MainTracker.track("custom", "Funnel WalkMe Test Lab", { Button_Click: ctaLabel });
    tryStore();
  };

  let tooltipStyle: React.CSSProperties;
  if (anchor) {
    const top = anchor.top + anchor.height + 10;
    let left = anchor.left - 8;
    const maxLeft = window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_PADDING;
    if (left > maxLeft) left = Math.max(VIEWPORT_PADDING, maxLeft);
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
    tooltipStyle = { top, left, width: TOOLTIP_WIDTH, boxShadow: TOOLTIP_SHADOW };
  } else {
    tooltipStyle = {
      top: 80,
      left: "50%",
      width: TOOLTIP_WIDTH,
      transform: "translateX(-50%)",
      boxShadow: TOOLTIP_SHADOW,
    };
  }

  const blurStyle: React.CSSProperties = {
    backdropFilter: BACKDROP_BLUR,
    WebkitBackdropFilter: BACKDROP_BLUR,
  };

  let backdrop: React.ReactNode;
  if (anchor) {
    // Clear "cutout" around the highlighted anchor; everything outside it is blurred + dimmed.
    const cutTop = Math.max(anchor.top - 4, 0);
    const cutLeft = Math.max(anchor.left - 8, 0);
    const cutWidth = anchor.width + 16;
    const cutHeight = anchor.height + 8;
    backdrop = (
      <>
        {/* Frosted frame: blur the page everywhere except the anchor cutout. */}
        <div className="absolute pointer-events-none" style={{ ...blurStyle, top: 0, left: 0, right: 0, height: cutTop }} />
        <div className="absolute pointer-events-none" style={{ ...blurStyle, top: cutTop + cutHeight, left: 0, right: 0, bottom: 0 }} />
        <div className="absolute pointer-events-none" style={{ ...blurStyle, top: cutTop, left: 0, width: cutLeft, height: cutHeight }} />
        <div className="absolute pointer-events-none" style={{ ...blurStyle, top: cutTop, left: cutLeft + cutWidth, right: 0, height: cutHeight }} />
        {/* Spotlight dim + green highlight border around the anchor. */}
        <div
          className="absolute rounded-[8px] border border-solid border-[#269c6e] pointer-events-none"
          style={{
            top: cutTop,
            left: cutLeft,
            width: cutWidth,
            height: cutHeight,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.4)",
          }}
        />
      </>
    );
  } else {
    backdrop = <div className="absolute inset-0 bg-black/40" style={blurStyle} />;
  }

  const content = (
    <div className="fixed inset-0 z-[100000]" onClick={dismiss}>
      {backdrop}

      <div
        className="absolute bg-white rounded-[8px] pt-[12px] pb-[16px] px-[22px]"
        style={tooltipStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-header font-semibold text-[14px] leading-[21px] text-[#363636] m-0">
          {title}
        </h3>
        <p className="font-header font-normal text-[12px] leading-[15px] text-[#98a0af] mt-[8px] mb-0">
          {body}
        </p>
        <div className="flex items-center justify-center gap-[6px] mt-[20px]">
          <button
            type="button"
            className="h-[36px] w-[132px] rounded-[4px] bg-transparent border-0 font-header font-normal text-[12px] tracking-[0.15px] text-[#61a333] underline cursor-pointer"
            onClick={handleDismissClick}
          >
            {dismissLabel}
          </button>
          <button
            type="button"
            className="h-[36px] w-[178px] rounded-[4px] border-0 bg-[#f7faef] font-header font-medium text-[12px] tracking-[0.15px] text-[#61a333] cursor-pointer"
            onClick={handleCtaClick}
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default MyAccountStoreWalkMe;
